<template>
  <div class="lang-delta-editor">
    <ClientOnly>
      <QuillEditor
        ref="quill"
        :placeholder="ls(placeholder)"
        @input="onInput"
      />
    </ClientOnly>
    <BaseIcon
      v-show="isEmptyDelta(delta)"
      class="editable-icon"
      name="mdi/pencil"
    />
    <div class="editor-lang is-hidden-mobile" v-if="!nolang">
      <UiIconRadioButton
          class="input-lang"
          :class="{ 'is-empty': isEmptyDelta(langDelta.ja) }"
          id="lang-delta-editor-ja"
          v-model="langValue"
          value="ja"
          arialabel="ja"
      >{{ ls({ ja: "日本語", en: "Jp" }) }}</UiIconRadioButton>
      <UiIconRadioButton
          class="input-lang"
          :class="{ 'is-empty': isEmptyDelta(langDelta.en) }"
          id="lang-delta-editor-en"
          v-model="langValue"
          value="en"
          arialabel="en"
      >{{ ls({ ja: "英語", en: "En" }) }}</UiIconRadioButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
const { ls, lang: stateLang } = useI18n()

interface Props {
  langDelta: LangDelta,
  placeholder?: LangString,
  nolang?: boolean,
}
const props = withDefaults(defineProps<Props>(), {
  nolang: false
})

const lang = computed((): string => {
  if (props.nolang) return 'ja'
  else return langValue.value
})

const langValue: Ref<string> = ref('ja')

watch(
  () => stateLang,
  (n) => {
    langValue.value = n.value
  }
)
watch(
  ()=> lang,
  (n) => {
    langValue.value = n.value
  },
  { deep: true }
)

const quill = ref(null)
const setContents = () => {
  nextTick(() => {
    if (quill.value) {
      quill.value.setContents(delta.value)
    }
  })
}

const delta = computed(() => {
  return lang.value === 'ja' ? props.langDelta.ja : props.langDelta.en
})

const watchLang = () => {
  setContents()
}
watch(
  ()=> lang,
  () => watchLang(),
  { deep: true }
)

const watchFlag: Ref<boolean> = ref(false)
watch(
  ()=> props.langDelta,
  () => {
    if (!watchFlag.value) {
      setContents()
    }
  },
  { deep: true }
)

const onInput = (m: Delta) => {
  // console.info('input')
  watchFlag.value = true
  if (lang.value === 'ja') props.langDelta.ja = m
  else props.langDelta.en = m
  //NextTickしないと無限ループ
  nextTick(() => {
    watchFlag.value = false
  })
}

onBeforeMount(() => {
  if (props.langDelta) {
    if (!props.langDelta.ja) props.langDelta.ja = createEmptyDelta()
    if (!props.langDelta.en) props.langDelta.en = createEmptyDelta()
  }
})

onMounted(() => {
  watchLang()
})
</script>

<style lang="scss">
@use 'sass:color';
@use '@/assets/styles/scss/vue' as *;

.lang-delta-editor {
  position: relative;
  transition: $transition;
  border-radius: $global-radius;
  &:hover:not(:focus-within) {
    background: $jps-gray-50;
    .editable-icon {
      color: color.adjust($jps-sub-orange, $lightness: -15%);
    }
  }

  .jps-quill-editor {
    padding: 12px;
    .ql-editor {
      padding: 0;
      min-height: 4rem;
    }
  }
  .ql-editor.ql-blank::before {
    padding-left: 1.2rem;
  }

  .editable-icon {
    position: absolute;
    left: 12px;
    top: 10px;
    font-size: 1rem;
    color: $jps-sub-orange;
  }
  &:focus-within {
    .editable-icon {
      display: none;
    }
    .ql-editor.ql-blank::before {
      display: none;
    }
  }
  @include focus-within();

  .editor-lang {
    position: absolute;
    background-color: rgba($white, 0.8);
    display: flex;
    padding: 6px 4px;
    bottom: 1px;
    right: 12px;
    z-index: 2;
    align-items: flex-end;
    line-height: 1 !important;

    .input-lang {
      @include no-decorative-button;
      padding-right: 3px;
      font-size: 0.75rem !important;
      font-weight: bold;
    }
  }
}
</style>
